import { useContext, useState } from "react";

import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ActiveElement, ChartData, ChartOptions } from "chart.js";
import { css } from "@emotion/react";

import { useGetFilterParams } from "components/Dashboard/hooks";
import ParamsContext from "context/params/paramsContext";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";
import { ACTIVITY_STATUS, FILTER_NAMES, ROUTES } from "shared/types/enums";
import { body1SemiBoldStyles, body2SemiBoldStyles } from "shared";

import { EMPTY, GAP, LOADING, TRANSPARENT_COLOR } from "../constants";
import { useColors } from "./hooks";
import Legend from "./Legend";
import { DoughnutChartProps } from "./types";

const DoughnutChart = ({ stats }: DoughnutChartProps) => {
    const { setQueryParams } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { statusColors, gray700, gray900 } = useColors();
    const { filterParams } = useGetFilterParams();
    const { renderFormattedNumber } = useFormattedNumber();
    const navigate = useNavigate();

    const [isHovered, setIsHovered] = useState(false);

    const [hoveredSlice, setHoveredSlice] = useState<ACTIVITY_STATUS | null>(
        null
    );

    const total = stats.reduce((acc, item) => acc + item.count, 0);

    const getActiveSliceName = (elements: ActiveElement[]) => {
        const activeElement = elements[0];
        const index = activeElement?.index;
        const activeDataItem = dataWithGaps[index];

        if (activeDataItem && !activeDataItem.name.includes(GAP)) {
            return activeDataItem.name as ACTIVITY_STATUS;
        } else {
            return null;
        }
    };

    const getGapValue = () => {
        const GAP_SIZE_PERCENT = 0.01;
        const value = total * GAP_SIZE_PERCENT;

        return Math.round(value * 100) / 100; // Round to two decimal places
    };

    const isSingleItem = stats.length === 1;
    const isEmpty = stats[0].name === EMPTY;
    const isLoading = stats[0].name === LOADING;
    const isReady = !isLoading && !isEmpty;

    const dataWithGaps = stats.flatMap((statsItem, index) =>
        isSingleItem
            ? statsItem
            : [statsItem, { name: `${GAP}-${index}`, count: getGapValue() }]
    );

    const dataCount = dataWithGaps.map(({ count }) => count);

    const bgColor = stats.flatMap(({ name }) => [
        statusColors[name].bg.rest,
        TRANSPARENT_COLOR
    ]);

    const borderColor = stats.flatMap(({ name }) => [
        statusColors[name].border.rest,
        TRANSPARENT_COLOR
    ]);

    const loaderColor = statusColors[LOADING].bg.rest;

    const doughnutData: ChartData<"doughnut"> = {
        labels: [],
        datasets: [
            {
                data: dataCount,
                backgroundColor: bgColor,
                hoverBackgroundColor: bgColor,
                borderColor: borderColor,
                hoverBorderColor: borderColor,
                borderRadius: isSingleItem ? 0 : 2,
                borderWidth: 1
            }
        ]
    };

    const doughnutOptions: ChartOptions<"doughnut"> = {
        responsive: false,
        cutout: "71%",
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: false
            }
        },
        onHover: (event, elements) => {
            const eventTarget = event.native?.target as HTMLElement | null;

            if (!isHovered || !eventTarget || !isReady) {
                handleSliceHover(null);
                return;
            }

            const sliceName = getActiveSliceName(elements);

            if (sliceName) {
                eventTarget.style.cursor = "pointer";
                handleSliceHover(sliceName);
            } else {
                resetHover(eventTarget);
            }
        },
        onClick: (_, elements) => {
            const sliceName = getActiveSliceName(elements);
            sliceName && isReady && handleSliceClick(sliceName);
        }
    };

    const handleSliceClick = (name: ACTIVITY_STATUS) => {
        const ids = {
            [ACTIVITY_STATUS.Online]: 2,
            [ACTIVITY_STATUS.Offline]: 1,
            [ACTIVITY_STATUS.Inactive]: 0
        };

        const id = ids[name];

        navigate(ROUTES.Devices);

        setQueryParams({
            params: {
                [FILTER_NAMES.ActivityStatus]: String(id),
                ...filterParams
            },
            resetParams: true
        });
    };

    const handlePointerEnter = () => setIsHovered(true);

    const handlePointerLeave = () => {
        setIsHovered(false);
        handleSliceHover(null);
    };

    const handleSliceHover = (slice: ACTIVITY_STATUS | null) =>
        setHoveredSlice(slice);

    const resetHover = (eventTarget: HTMLElement) => {
        eventTarget.style.cursor = "default";
        handleSliceHover(null);
    };

    return (
        <div
            css={css({
                display: "flex",
                marginBottom: "16px"
            })}
        >
            <div
                css={css({
                    position: "relative",
                    width: "142px",
                    height: "142px",

                    "@keyframes pulse": {
                        "0%": {
                            opacity: "1"
                        },

                        "50%": {
                            opacity: "0.4"
                        },

                        "100%": {
                            opacity: "1"
                        }
                    },

                    "& > canvas": {
                        position: "absolute",
                        animation: isLoading
                            ? "pulse 2s ease-in-out 0.5s infinite"
                            : "none"
                    }
                })}
            >
                <Doughnut
                    onPointerEnter={handlePointerEnter}
                    onPointerLeave={handlePointerLeave}
                    data={doughnutData}
                    options={doughnutOptions}
                    width={142}
                    height={142}
                />

                {isLoading ? (
                    <div
                        css={css({
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%"
                        })}
                    >
                        <div
                            css={css({
                                "@keyframes dots": {
                                    "20%": {
                                        backgroundPosition:
                                            "0% 0%, 50% 50%, 100% 50%"
                                    },
                                    "40%": {
                                        backgroundPosition:
                                            "0% 100%, 50% 0%, 100% 50%"
                                    },
                                    "60%": {
                                        backgroundPosition:
                                            "0% 50%, 50% 100%, 100% 0%"
                                    },
                                    "80%": {
                                        backgroundPosition:
                                            "0% 50%, 50% 50%, 100% 100%"
                                    }
                                },

                                width: "60px",
                                aspectRatio: "3",
                                background: `no-repeat radial-gradient(circle closest-side, ${loaderColor} 90%, #0000) 0% 50%, no-repeat radial-gradient(circle closest-side, ${loaderColor} 90%, #0000) 50% 50%, no-repeat radial-gradient(circle closest-side, ${loaderColor} 90%, #0000) 100% 50%`,
                                backgroundSize: "calc(100% / 3) 50%",
                                animation: "dots 1s infinite linear"
                            })}
                        />
                    </div>
                ) : (
                    <div
                        css={css({
                            whiteSpace: "nowrap",
                            margin: "51px 24px",

                            "& > div": {
                                textAlign: "center",
                                overflowX: "hidden",
                                textOverflow: "ellipsis"
                            },

                            "& > div:first-of-type": {
                                ...body1SemiBoldStyles,
                                color: gray900
                            },

                            "& > div:last-of-type": {
                                ...body2SemiBoldStyles,
                                color: gray700
                            }
                        })}
                    >
                        <div>{isEmpty ? 0 : renderFormattedNumber(total)}</div>
                        <div>{t("General##devices")}</div>
                    </div>
                )}
            </div>

            <Legend
                stats={stats}
                isLoading={isLoading}
                isEmpty={isEmpty}
                hoveredSlice={hoveredSlice}
                onHover={handleSliceHover}
                onClick={handleSliceClick}
            />
        </div>
    );
};

export default DoughnutChart;
